import React, { useState, useEffect, useCallback } from "react";
import {
  getMissionEndAPI,
  updateMissionEndAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import "./AdEndHistoryModal.css";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = [
  { value: "", label: "전체" },
  { value: "PENDING", label: "승인대기" },
  { value: "APPROVED", label: "승인" },
  { value: "REJECTED", label: "거절" },
  { value: "CANCELLED", label: "취소" },
  { value: "DONE", label: "적용 완료" },
];

const getStatusLabel = (status) => {
  const map = {
    PENDING: "승인대기",
    APPROVED: "승인",
    REJECTED: "거절",
    CANCELLED: "취소",
    DONE: "적용 완료",
  };
  return map[status] ?? status ?? "-";
};

const AdEndHistoryModal = ({ isOpen, onClose, webMemberId }) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [filters, setFilters] = useState({
    missionName: "",
    status: "",
    appliedDateStart: "",
    appliedDateEnd: "",
  });

  const fetchData = useCallback(
    async (overridePage = null) => {
      if (!webMemberId || !isOpen) return;

      const currentPage = overridePage !== null ? overridePage : page;
      setLoading(true);
      setError(null);
      try {
        const params = {
          webMemberId: Number(webMemberId),
          page: currentPage,
          pageSize,
        };
        if (filters.missionName?.trim())
          params.missionName = filters.missionName.trim();
        if (filters.status) params.status = filters.status;
        if (filters.appliedDateStart)
          params.appliedDateStart = filters.appliedDateStart;
        if (filters.appliedDateEnd)
          params.appliedDateEnd = filters.appliedDateEnd;

        const response = await getMissionEndAPI(params);
        const resData = response?.data;
        const list = Array.isArray(resData?.data) ? resData.data : [];
        const totalCount =
          resData?.total ?? resData?.pagination?.total ?? list.length;
        setData(list);
        setTotal(totalCount);
      } catch (err) {
        console.error("광고 종료 신청 내역 조회 실패:", err);
        setError(
          err?.response?.data?.msg ||
            "광고 종료 신청 내역을 불러오는데 실패했습니다."
        );
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      webMemberId,
      isOpen,
      page,
      pageSize,
      filters.missionName,
      filters.status,
      filters.appliedDateStart,
      filters.appliedDateEnd,
    ]
  );

  useEffect(() => {
    if (isOpen && webMemberId) {
      fetchData();
    }
  }, [isOpen, webMemberId, fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(1);
  };

  const handleDelete = async (row) => {
    const id = row?.id ?? row?._id;
    if (id == null || row?.status !== "PENDING") return;
    if (!window.confirm("이 광고 종료 신청을 취소하시겠습니까?")) return;
    setDeletingId(id);
    try {
      await updateMissionEndAPI({ id, status: "CANCELLED" });
      await fetchData(page);
    } catch (err) {
      console.error("광고 종료 신청 취소 실패:", err);
      const msg = err?.response?.data?.msg || "취소에 실패했습니다.";
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const str = String(dateString).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str.replace(/-/g, ".");
    try {
      const korea = convertUTCToKoreaTime(dateString);
      if (korea) return korea.toISOString().slice(0, 10).replace(/-/g, ".");
    } catch (e) {}
    return str.replace(/-/g, ".");
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (!isOpen) return null;

  return (
    <div className="aeh-modal-overlay" onClick={onClose}>
      <div className="aeh-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aeh-modal-header">
          <h2 className="aeh-modal-title">광고 종료 신청 내역</h2>
          <button
            type="button"
            className="aeh-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div className="aeh-modal-filters">
          <input
            type="text"
            className="aeh-filter-input"
            placeholder="광고명 검색"
            value={filters.missionName}
            onChange={(e) => handleFilterChange("missionName", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <select
            className="aeh-filter-select"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            className="aeh-filter-date"
            value={filters.appliedDateStart}
            onChange={(e) =>
              handleFilterChange("appliedDateStart", e.target.value)
            }
          />
          <span className="aeh-filter-sep">~</span>
          <input
            type="date"
            className="aeh-filter-date"
            value={filters.appliedDateEnd}
            onChange={(e) =>
              handleFilterChange("appliedDateEnd", e.target.value)
            }
          />
          <button type="button" className="aeh-filter-btn" onClick={handleSearch}>
            검색
          </button>
        </div>

        <div className="aeh-modal-body">
          {loading ? (
            <div className="aeh-loading">불러오는 중...</div>
          ) : error ? (
            <div className="aeh-error">{error}</div>
          ) : (
            <div className="aeh-table-wrap">
              <table className="aeh-table">
                <thead>
                  <tr>
                    <th>광고 명</th>
                    <th>접수 날짜</th>
                    <th>희망 광고 종료일</th>
                    <th>기존 종료일</th>
                    <th>상태</th>
                    <th className="aeh-th-delete">취소</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="aeh-empty">
                        광고 종료 신청 내역이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    data.map((row) => (
                      <tr
                        key={
                          row._id ||
                          row.id ||
                          row.ad_id + "-" + (row.createdAt || "")
                        }
                      >
                        <td className="aeh-cell-mission">
                          {row.missionName || "-"}
                        </td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{formatDate(row.newEndDate ?? row.appliedDate)}</td>
                        <td>{formatDate(row.beforeEndDate)}</td>
                        <td>
                          <span
                            className={`aeh-status aeh-status-${(
                              row.status || ""
                            ).toLowerCase()}`}
                          >
                            {getStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="aeh-cell-delete">
                          {row.status === "PENDING" ? (
                            <button
                              type="button"
                              className="aeh-delete-btn"
                              onClick={() => handleDelete(row)}
                              disabled={deletingId === (row.id ?? row._id)}
                              title="취소"
                              aria-label="취소"
                            >
                              ×
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="aeh-modal-footer">
          <div className="aeh-pagination">
            <button
              type="button"
              className="aeh-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              이전
            </button>
            <span className="aeh-page-info">
              {page} / {totalPages} (총 {total}건)
            </span>
            <button
              type="button"
              className="aeh-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdEndHistoryModal;
